import { Box, Stack, Tab, Tabs } from "@mui/material";
import { ConnectedBankAccounts } from "Components/Users/ConnectedBankAccounts/ConnectedBankAccounts";
import { ExportTransactionsButton } from "Components/Shared/ExportTransactionsButton";
import { PaperTitle } from "Components/Shared/PaperTitle";
import { PrimaryPaper } from "Components/Shared/PrimaryPaper";
import { UserTransactionFees } from "Components/Transactions/UserTransactionFees/UserTransactionFees";
import { Licenses } from "Components/Users/UserDetailPageContent/Components/LicenseForm/Licenses";
import { usePartyQuery } from "Api/Queries/Party/usePartyQuery";
import { PartyTabContent } from "Components/Users/UserDetailPageContent/Components/PartyTabContent";
import { RelatedParties } from "Components/Users/UserDetailPageContent/Components/RelatedPartyForm/RelatedParties";
import { UserForm } from "Components/Users/UserDetailPageContent/Components/UserForm";
import { UserReadOnlyDetail } from "Components/Users/UserDetailPageContent/Components/UserReadOnlyDetail/UserReadOnlyDetail";
import { VerificationForm } from "Components/Users/UserDetailPageContent/Components/VerificationForm/VerificationForm";
import { isAccessRoleCode } from "Models/AccessRoleCodes";
import { useIsCurrentUserAdmin } from "Hooks/useIsCurrentUserAdmin";
import { useCheckPermission } from "Hooks/usePermission";
import {
  type PartyDto,
  PartyType,
  Permission,
  type UserAdminDetailDto,
  type UserVerificationUpdateDto,
} from "Infrastructure/Api/Api";
import { AccessRightCodes } from "Models/AccessRightCodes";
import { AccessRoleCodes } from "Models/AccessRoleCodes";
import { useSearchParams } from "Navigation/useSearchParams";
import type { ReactNode } from "react";
import { Resources, useResource } from "Translations/Resources";
import { isSettlementUserRole } from "Types/settlements";

const COMPONENT_RESOURCES = Resources.Pages.Content.UserDetailPageContent.Tabs;

export type UserDetailPageTab =
  | "user"
  | "party"
  | "bankAccounts"
  | "licenses"
  | "verification"
  | "settlement"
  | "relatedParties";

type TabItem = {
  id: UserDetailPageTab;
  label: string;
  content: ReactNode;
  isHidden?: boolean;
};

type Props = {
  party: PartyDto;
  user: UserAdminDetailDto;
  verification: UserVerificationUpdateDto;
  legacySourceOfIncome?: string | null;
};

export const UserDetailPageContent = ({
  user,
  verification,
  party,
  legacySourceOfIncome,
}: Props) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const userRole = isAccessRoleCode(user.accessRoleCode)
    ? user.accessRoleCode
    : null;

  // A self-added hunter has no Hunter User row — the role grant (and the business Party holding
  // the license) comes from the Identity model instead of the legacy access role.
  const identityHunterRole = user.identityRoles.find(
    (role) =>
      role.accessRoleCode === AccessRoleCodes.Hunter ||
      role.accessRoleCode === AccessRoleCodes.HunterPlus,
  );
  const identityHunterPartyPublicID =
    identityHunterRole?.partyPublicID &&
    identityHunterRole.partyPublicID !== party.publicID
      ? identityHunterRole.partyPublicID
      : undefined;
  const { data: hunterBusinessParty } = usePartyQuery(
    identityHunterPartyPublicID,
  );
  const licenseParty = hunterBusinessParty ?? party;
  const { t } = useResource();
  const canExportTransactions = useCheckPermission(
    AccessRightCodes.Transactions,
    Permission.Export,
  );
  const isAdmin = useIsCurrentUserAdmin();

  const displayName =
    party.companyName ??
    ([party.firstName, party.lastName].filter(Boolean).join(" ") ||
      user.userID);

  const isRelatedPartiesHidden = party.type !== PartyType.LegalEntity;

  // Settlement keys on the role's Party — for a self-added hunter that is the Identity grant's
  // business Party, and the role itself comes from the grant instead of the legacy access role.
  const identityHunterRoleCode =
    identityHunterRole && isAccessRoleCode(identityHunterRole.accessRoleCode)
      ? identityHunterRole.accessRoleCode
      : null;
  const settlementRole = isSettlementUserRole(userRole)
    ? userRole
    : isSettlementUserRole(identityHunterRoleCode)
      ? identityHunterRoleCode
      : null;
  const settlementPartyPublicID = isSettlementUserRole(userRole)
    ? user.partyPublicID
    : (identityHunterRole?.partyPublicID ?? user.partyPublicID);
  const userTabContent = isAdmin ? (
    <UserForm user={user} />
  ) : (
    <UserReadOnlyDetail user={user} />
  );

  const tabs: Array<TabItem> = [
    {
      id: "user",
      label: t(COMPONENT_RESOURCES.User.Title),
      content: userTabContent,
    },
    {
      id: "party",
      label: t(COMPONENT_RESOURCES.Party.Title),
      content: (
        <PartyTabContent
          party={party}
          userID={user.userID}
          currentVerificationStatus={
            user.verificationIdentityVerificationStatus
          }
          isUboUnknown={verification.uboUnknown ?? false}
        />
      ),
    },
    {
      id: "bankAccounts",
      label: t(COMPONENT_RESOURCES.BankAccounts.Title),
      content: (
        <PrimaryPaper>
          <PaperTitle>
            {t(COMPONENT_RESOURCES.BankAccounts.Subtitle)}
          </PaperTitle>
          <Stack mt={5} gap={5}>
            <ConnectedBankAccounts
              userID={user.userID}
              verifiedIBAN={user.verifiedIBAN}
              bankVerificationSource={user.bankVerificationSource}
              firstName={party.firstName}
              lastName={party.lastName}
              companyName={party.companyName}
              bankVerificationDate={user.bankVerificationDate}
            />
            {canExportTransactions && (
              <ExportTransactionsButton
                fileNamePrefix={`${t(Resources.Components.Transactions.Title)}_${displayName}`}
                userID={user.userID}
              />
            )}
          </Stack>
        </PrimaryPaper>
      ),
    },
    {
      id: "licenses",
      label: t(COMPONENT_RESOURCES.Licenses.Title),
      content: (
        <Licenses
          licenses={licenseParty.licenses}
          partyPublicID={licenseParty.publicID || ""}
        />
      ),
      isHidden:
        user.accessRoleCode !== AccessRoleCodes.Hunter &&
        user.accessRoleCode !== AccessRoleCodes.HunterPlus &&
        !identityHunterRole,
    },
    {
      id: "verification",
      label: t(COMPONENT_RESOURCES.Verification.Title),
      content: (
        <VerificationForm
          verification={verification}
          legacySourceOfIncome={legacySourceOfIncome}
        />
      ),
    },
    ...(settlementRole
      ? [
          {
            id: "settlement" as const,
            label: t(COMPONENT_RESOURCES.Settlement.Title),
            content: (
              <UserTransactionFees
                partyPublicID={settlementPartyPublicID}
                userID={user.userID}
                userRole={settlementRole}
              />
            ),
          },
        ]
      : []),
    {
      id: "relatedParties",
      label: t(COMPONENT_RESOURCES.RelatedParties.Title),
      content: (
        <PrimaryPaper>
          <RelatedParties
            relatedParties={party.relatedParties}
            owningPartyID={party.publicID}
          />
        </PrimaryPaper>
      ),
      isHidden: isRelatedPartiesHidden,
    },
  ];
  const visibleTabs = tabs.filter(({ isHidden }) => !isHidden);

  // The URL may carry a tab that is hidden for this user (or garbage) — rendering
  // it would select nothing and show an empty page.
  const requestedTab = searchParams.get("activeTab");
  const isRequestedTabVisible = visibleTabs.some(
    ({ id }) => id === requestedTab,
  );
  const activeTab = isRequestedTabVisible
    ? requestedTab
    : visibleTabs[0]?.id || "user";

  return (
    <>
      <Tabs
        value={activeTab}
        onChange={(_e, id: string) => setSearchParams({ activeTab: id })}
        variant="scrollable"
        scrollButtons="auto"
      >
        {visibleTabs.map(({ label, id }) => (
          <Tab key={id} label={label} value={id} />
        ))}
      </Tabs>
      <Box mt="0.5rem">
        {visibleTabs.map(({ content, id }) => (
          <div role="tabpanel" hidden={id !== activeTab} key={id}>
            {id === activeTab && content}
          </div>
        ))}
      </Box>
    </>
  );
};
